import { type JSX, useState } from "react"
import { useParams } from "react-router"
import styled from "styled-components"

import { type PersonalizedCourse } from "@forento/shared/models/course"
import { parseNumber } from "@forento/shared/utilities/number"

import { useCourseDetail } from "~/api/course"
import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import Button from "~/themes/original/components/Button"
import CourseComments from "~/themes/original/components/CourseComments"
import FullLoadingPage from "~/themes/original/components/FullLoadingPage"
import Hero, { CallToActionIcon, CallToActionInfo } from "~/themes/original/components/Hero"
import { ArrowIcon } from "~/themes/original/components/Icon"
import Layout from "~/themes/original/components/Layout"
import PurchaseItem from "~/themes/original/components/PurchaseItem"
import { ControlledTabs } from "~/themes/original/components/Tabs"
import NotFoundPage from "~/themes/original/pages/NotFoundPage"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

import ChaptersList from "./ChaptersList"
import Resources from "./Resources"

const CourseDetailPage: React.FC = () => {
	const courseId = parseNumber(useParams().courseId!)

	const [data, reload] = useCourseDetail(courseId ?? -1)
	const t = useTranslation()

	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	switch (data.status) {
		case "error":
			return <Layout>Failed to load course.</Layout>
		case "loading":
			return <FullLoadingPage />
		case "not-found":
			return <NotFoundPage />
		case "success": {
			const course = data.course

			const callToAction: JSX.Element = (() => {
				switch (data.button.variant) {
					case "start":
						return (
							<Button variant="primary" onClick={routes.course.player(course.id)}>
								<CallToActionIcon as={ArrowIcon} />
								Start
							</Button>
						)
					case "continue":
						return (
							<Button variant="primary" onClick={routes.course.player(course.id)}>
								<CallToActionIcon as={ArrowIcon} />
								Continue
							</Button>
						)
					case "purchase":
						return (
							<PurchaseItem
								item={{
									type: "course",
									id: course.id,
									name: course.title,
									shortDescription: course.shortDescription,
									price: data.button.price,
								}}
								onPurchase={async ({ paypalOrder }) => {
									setPaypalOrder(paypalOrder)
									await reload()
								}}
							/>
						)
					case "membership":
						return (
							<>
								<CallToActionInfo>This course is only available through a membership.</CallToActionInfo>
								<Button variant="primary" onClick={routes.membership.index()}>
									Membership
								</Button>
							</>
						)
					case "paused":
						return <CallToActionInfo>{t("product.paused")}</CallToActionInfo>
				}
			})()

			return (
				<PurchaseSuccessModalContextProvider
					content={{ type: "course", id: course.id }}
					paypalOrder={paypalOrder}
				>
					<Layout
						metadata={{
							title: data.title,
							description: course.shortDescription,
							imageUrl: course.thumbnailFilePath ?? undefined,
						}}
					>
						<Hero
							title={course.title}
							htmlDescription={course.shortDescription}
							callToAction={callToAction}
							thumbnailUrl={course.thumbnailFilePath}
						/>
						<StyledTabs tabs={getTabs(course, reload)} />
					</Layout>
				</PurchaseSuccessModalContextProvider>
			)
		}
	}
}

function getTabs(course: PersonalizedCourse, reloadCourse: () => Promise<unknown>) {
	const tabs = []
	if (course.presentationHtml !== null) {
		tabs.push({
			title: "Overview",
			content: (
				<TabContent>
					<div className="editorjs" dangerouslySetInnerHTML={{ __html: course.presentationHtml }} />
				</TabContent>
			),
		})
	}
	tabs.push({
		title: "Content",
		content: (
			<TabContent>
				<ChaptersList chapters={course.chapters} />
			</TabContent>
		),
	})
	if (course.status === "unlocked" && course.resources.length > 0) {
		tabs.push({
			title: "Resources",
			content: (
				<TabContent>
					<Resources resources={course.resources} />
				</TabContent>
			),
		})
	}
	if (course.commentsEnabled) {
		tabs.push({
			title: "Comments",
			content: (
				<TabContent>
					<CourseComments
						courseId={course.id}
						commentsUnderReview={course.status === "unlocked" ? course.commentsUnderReview : 0}
						pageId={null}
						reloadCourse={reloadCourse}
					/>
				</TabContent>
			),
		})
	}
	return tabs
}

const StyledTabs = styled(ControlledTabs)`
	@media (max-width: 999px) {
		margin: 0 16px;
	}
`

const TabContent = styled.div`
	margin-top: 32px;
`

export default CourseDetailPage
